import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Smartphone, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { PUNCHLINE } from "@/components/dealbook/shared";

const inrFull = (v) => `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(v || 0)}`;

export default function PayNow() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [claimError, setClaimError] = useState(false);

  useEffect(() => {
    api.get(`/public/pay/${token}`).then((r) => {
      setInfo(r.data);
      if (r.data.payment_claim?.at) setClaimed(r.data.payment_claim.at);
    }).catch(() => setError(true));
  }, [token]);

  const confirmPaid = async () => {
    setClaiming(true);
    setClaimError(false);
    try {
      const r = await api.post(`/public/pay/${token}/confirm`);
      setClaimed(r.data.claimed_at || "now");
    } catch {
      setClaimError(true);
    } finally {
      setClaiming(false);
    }
  };

  const upiLink = info?.upi_id
    ? `upi://pay?pa=${encodeURIComponent(info.upi_id)}&pn=${encodeURIComponent(info.firm)}&am=${info.total}&cu=INR&tn=${encodeURIComponent(`Invoice ${info.invoice_no}`)}`
    : null;

  return (
    <div className="min-h-screen bg-[#0A0A0B] font-plex flex items-center justify-center px-6 py-12" data-testid="pay-page">
      <div className="w-full max-w-md border border-white/10 bg-[#111112] p-8">
        <div className="flex flex-col items-center text-center pb-6 border-b border-white/10">
          <img src="/logo.jpeg" alt="Keyscape Advisory Services LLP" className="h-12 w-auto rounded-md bg-white px-2 py-1 object-contain" />
          <p className="text-sm font-medium text-white mt-3">Keyscape Advisory Services LLP</p>
          <p className="text-[11px] italic text-[#D9A441] mt-1" data-testid="pay-punchline">{PUNCHLINE}</p>
        </div>

        {error && (
          <div className="text-center py-10" data-testid="pay-error">
            <AlertCircle size={20} className="text-[#F04864] mx-auto" />
            <p className="text-white mt-3">This payment link is invalid or has expired.</p>
            <p className="text-sm text-neutral-500 mt-1">Please contact Keyscape Advisory for a fresh invoice link.</p>
          </div>
        )}

        {!error && !info && (
          <p className="font-plexmono text-[10px] uppercase tracking-[0.3em] text-neutral-500 animate-pulse text-center py-10" data-testid="pay-loading">Loading invoice…</p>
        )}

        {info && (
          <div className="pt-6 space-y-5" data-testid="pay-details">
            <div className="text-center">
              <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">Invoice {info.invoice_no}</p>
              <p className="text-4xl font-light text-[#D9A441] mt-3" data-testid="pay-amount">{inrFull(info.total)}</p>
              <p className="font-plexmono text-[9px] text-neutral-600 mt-1 uppercase tracking-[0.15em]">incl. GST @ 18%</p>
              <p className="text-sm text-neutral-400 mt-3">Billed to <span className="text-white">{info.billed_to}</span></p>
            </div>

            {info.payment_status === "Paid" ? (
              <p className="border border-[#5C8A6E]/40 bg-[#5C8A6E]/10 text-[#5C8A6E] text-center font-plexmono text-[10px] uppercase tracking-[0.2em] px-4 py-3" data-testid="pay-paid">This invoice is marked Paid — thank you!</p>
            ) : upiLink ? (
              <>
                <a href={upiLink} data-testid="pay-upi-btn" className="flex items-center justify-center gap-2 bg-[#D9A441] hover:bg-[#c8943a] text-black font-plexmono text-[11px] uppercase tracking-[0.2em] font-semibold px-6 py-4 transition-colors">
                  <Smartphone size={15} /> Pay via UPI
                </a>
                <p className="text-center font-plexmono text-[10px] text-neutral-500" data-testid="pay-upi-id">
                  UPI ID: <span className="text-neutral-300">{info.upi_id}</span>
                </p>
                <p className="text-center text-xs text-neutral-600">On your phone, the button opens your UPI app with the amount pre-filled.</p>
              </>
            ) : info.payment_terms ? (
              <p className="text-center text-sm text-neutral-400" data-testid="pay-manual">Please pay using the bank details below.</p>
            ) : (
              <p className="text-center text-sm text-neutral-400" data-testid="pay-manual">Please contact Keyscape Advisory for payment details.</p>
            )}

            {info.payment_terms && (
              <p className="border-t border-white/10 pt-4 text-center font-plexmono text-[10px] text-neutral-500 leading-relaxed" data-testid="pay-terms">{info.payment_terms}</p>
            )}

            {info.payment_status !== "Paid" && (
              claimed ? (
                <p className="border border-[#5C8A6E]/40 bg-[#5C8A6E]/10 text-[#5C8A6E] text-center font-plexmono text-[10px] uppercase tracking-[0.2em] px-4 py-3 leading-relaxed" data-testid="pay-confirm-done">
                  Payment reported — thank you! Keyscape Advisory will verify and confirm shortly.
                </p>
              ) : (
                <div className="space-y-2">
                  <button onClick={confirmPaid} disabled={claiming} data-testid="pay-confirm-btn" className="w-full border border-white/20 hover:border-[#D9A441] text-neutral-300 hover:text-[#D9A441] font-plexmono text-[10px] uppercase tracking-[0.2em] px-4 py-3 transition-colors disabled:opacity-50">
                    {claiming ? "Reporting…" : "I've made the payment"}
                  </button>
                  {claimError && (
                    <p className="text-center text-xs text-[#F04864]" data-testid="pay-confirm-error">Could not report the payment — please try again.</p>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
